import { Injectable, OnDestroy, inject, isDevMode } from '@angular/core';
import { EventLoggerService } from '@services/event-logger-service';
import { ExperimentService } from '@services/experiment/experiment.service';
import { AppConfig } from '../../app/app.config';
import { ScrollDepthInitOptions, ScrollDepthPageName } from './scroll-depth.types';

export * from './scroll-depth.types';

export const SCROLL_DEPTH_EVENT = 'scroll_depth';

// CleverTap silently truncates property values around 1024 chars; stay under with margin.
const SECTIONS_VALUE_LIMIT = 1000;
const SECTIONS_WARN_LIMIT = SECTIONS_VALUE_LIMIT * 0.8;
// Auto-enable tracking if the page never calls pageReady().
const PAGE_READY_FALLBACK_MS = 3000;

/**
 * Page-scoped accumulator for scroll-depth tracking. Provide it in the page
 * component's `providers` array (NEVER providedIn: 'root') — Angular destroys it
 * with the page component, which is what triggers the event flush.
 */
@Injectable()
export class ScrollDepthService implements OnDestroy {
  private readonly eventLogger = inject(EventLoggerService);
  private readonly experimentService = inject(ExperimentService);
  private readonly appConfig = inject(AppConfig);

  private pageName: ScrollDepthPageName;
  private readonly seenSections: string[] = [];
  private flushed = false;
  private ready = false;
  private readonly readyCallbacks: Array<() => void> = [];
  private readonly insetCallbacks: Array<(insetPx: number) => void> = [];
  private readyFallbackTimer: ReturnType<typeof setTimeout> | null = null;
  bottomInsetPx = 0;

  init(pageName: ScrollDepthPageName, options?: ScrollDepthInitOptions): void {
    this.pageName = pageName;
    this.bottomInsetPx = options?.bottomInsetPx ?? 0;
    if (!this.ready && this.readyFallbackTimer === null) {
      this.readyFallbackTimer = setTimeout((): void => this.pageReady(), PAGE_READY_FALLBACK_MS);
    }
  }

  pageReady(): void {
    if (this.ready) return;
    this.ready = true;
    this.clearReadyFallbackTimer();
    this.readyCallbacks.splice(0).forEach((callback: () => void): void => callback());
  }

  isReady(): boolean {
    return this.ready;
  }

  onReady(callback: () => void): void {
    if (this.ready) {
      callback();
      return;
    }
    this.readyCallbacks.push(callback);
  }

  setBottomInset(insetPx: number): void {
    if (insetPx === this.bottomInsetPx) return;
    this.bottomInsetPx = insetPx;
    this.insetCallbacks.forEach((callback: (insetPx: number) => void): void => callback(insetPx));
  }

  onBottomInsetChange(callback: (insetPx: number) => void): void {
    this.insetCallbacks.push(callback);
  }

  markSeen(sectionName: string): void {
    if (this.seenSections.includes(sectionName)) return;
    this.seenSections.push(sectionName);
  }

  ngOnDestroy(): void {
    this.clearReadyFallbackTimer();
    if (this.flushed || !this.pageName || !this.seenSections.length) return;
    if (!this.experimentService.isScrollDepthTriggerEnabled(this.pageName)) return;
    this.flushed = true;
    const sectionsCsv = this.seenSections.join(',');
    if (isDevMode() && sectionsCsv.length >= SECTIONS_WARN_LIMIT) {
      // eslint-disable-next-line no-console
      console.warn(`[ScrollDepthService] sections CSV at ${sectionsCsv.length} chars — nearing the ${SECTIONS_VALUE_LIMIT} CleverTap value limit; keep section names short.`);
    }
    const payload: Record<string, string> = { pageName: this.pageName, sections: sectionsCsv };
    // Chunking is written but DISABLED pending an owner decision. To enable, replace
    // the `sections` assignment above with:
    // this.buildSectionChunks().forEach((chunk: string, index: number): void => {
    //   payload[index ? `sections_${index + 1}` : 'sections'] = chunk;
    // });
    this.eventLogger.cleverTapEvent(SCROLL_DEPTH_EVENT, JSON.stringify(payload), [
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.MOENGAGE,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
  }

  private clearReadyFallbackTimer(): void {
    if (this.readyFallbackTimer === null) return;
    clearTimeout(this.readyFallbackTimer);
    this.readyFallbackTimer = null;
  }

  // Splits the seen-sections list into <=SECTIONS_VALUE_LIMIT CSV chunks cut only at
  // whole-name boundaries (payload keys: sections, sections_2, ...). DISABLED pending
  // an owner decision — see the commented call in ngOnDestroy.
  // private buildSectionChunks(): string[] {
  //   const csv = this.seenSections.join(',');
  //   if (csv.length <= SECTIONS_VALUE_LIMIT) return [csv];
  //   const chunks: string[] = [];
  //   let current = '';
  //   this.seenSections.forEach((name: string): void => {
  //     const candidate = current ? `${current},${name}` : name;
  //     if (candidate.length > SECTIONS_VALUE_LIMIT && current) {
  //       chunks.push(current);
  //       current = name;
  //     } else {
  //       current = candidate;
  //     }
  //   });
  //   if (current) chunks.push(current);
  //   return chunks;
  // }
}
